import { DotsFilledIcon } from '@approvalmax/ui';
import { Button, Dropdown, Menu, Popup, toast } from '@approvalmax/ui/src/components';
import { errorHelpers } from '@approvalmax/utils';
import { domain } from 'modules/data';
import { FC, memo, useCallback, useEffect } from 'react';
import { useToggle } from 'react-use';
import { amplitudeService } from 'services/amplitude';
import { useDeleteXeroBudget, useToggleXeroBudget } from 'shared/data/webApp/v1';

import { useDownloadBudget } from '../../hooks/useDownloadBudget';
import { useSyncXeroBudgetMutation } from '../../hooks/useSyncXeroBudgetMutation';
import BudgetSettingsPopupContent from '../BudgetSettingsPopupContent/BudgetSettingsPopupContent';
import ReuploadPopupContent from '../ReuploadPopupContent/ReuploadPopupContent';
import TrackingCategoriesPopup from '../TrackingCategoriesPopupContent/TrackingCategoriesPopupContent';
import { messages } from './BudgetActionsMenu.messages';
import { BudgetActionsMenuProps } from './BudgetActionsMenu.types';

const BudgetActionsMenu: FC<BudgetActionsMenuProps> = memo((props) => {
    const { budget, company, isLoading: isExternalLoading, setIsLoading } = props;

    const companyId = company.id;
    const budgetId = budget.id;
    const integrationType = company.integration?.integrationType;

    const { isReadonly } = company;
    const { isEnabled, syncStatus, origin } = budget;
    const { hasActiveIntegration, isAuditor } = company.flags;
    const isNeedReview = syncStatus === domain.BudgetSyncStatus.NeedReview;

    const [isShowReuploadPopup, toggleIsShowReuploadPopup] = useToggle(false);
    const [isShowSettingsPopup, toggleIsShowSettingsPopup] = useToggle(false);

    const { syncXeroBudget, isLoadingSyncXeroBudget, isSuccessSyncXeroBudget } = useSyncXeroBudgetMutation();
    const { downloadBudget } = useDownloadBudget();
    const {
        mutate: deleteXeroBudget,
        isSuccess: isSuccessDeleteXeroBudget,
        isLoading: isLoadingDeleteXeroBudget,
    } = useDeleteXeroBudget();
    const { mutate: toggleXeroBudget } = useToggleXeroBudget();

    const onRefresh = useCallback(
        () => syncXeroBudget({ companyId, budgetId }, integrationType),
        [budgetId, integrationType, companyId, syncXeroBudget]
    );
    const onDownloadCsv = useCallback(() => downloadBudget(budgetId, companyId), [budgetId, companyId, downloadBudget]);
    const onRemove = useCallback(() => {
        if (!integrationType) {
            throw errorHelpers.notImplementedError();
        }

        // TODO: Create a confirmation component in another task
        if (window.confirm(messages.confirmRemove)) {
            setIsLoading(true);
            deleteXeroBudget({ body: { companyId, budgetId } });
        }
    }, [budgetId, companyId, integrationType, deleteXeroBudget, setIsLoading]);
    const onToggleBudgetIsEnabled = useCallback(() => {
        if (isNeedReview) return toggleIsShowSettingsPopup();
        if (!isEnabled) amplitudeService.sendData('budgets: active budget');

        if (!integrationType) {
            throw errorHelpers.notImplementedError();
        }

        toggleXeroBudget(
            { body: { companyId, budgetId, isEnabled: !isEnabled } },
            {
                onSuccess: ({ data }) => {
                    toast.success(data.isEnabled ? messages.hasBeenActivated : messages.hasBeenDeactivated);
                },
            }
        );
    }, [isNeedReview, toggleIsShowSettingsPopup, isEnabled, toggleXeroBudget, companyId, budgetId, integrationType]);

    useEffect(() => {
        if (isSuccessDeleteXeroBudget) {
            toast.success(messages.successRemove);
        }
    }, [isSuccessDeleteXeroBudget]);

    useEffect(() => {
        setIsLoading(isLoadingSyncXeroBudget);
    }, [isLoadingSyncXeroBudget, setIsLoading]);

    useEffect(() => {
        if (isSuccessSyncXeroBudget) {
            setIsLoading(false);
        }
    }, [isSuccessSyncXeroBudget, setIsLoading]);

    if (isExternalLoading || isLoadingSyncXeroBudget || isLoadingDeleteXeroBudget) return null;

    return (
        <>
            <Dropdown
                width='max-content'
                closeOnClickInnerButton
                activator={
                    <Button icon noPadding variant='text' color='blue100'>
                        <DotsFilledIcon size={24} />
                    </Button>
                }
            >
                <Menu size='small'>
                    <Menu.Item
                        name={isEnabled ? messages.deactivateMenuName : messages.activateMenuName}
                        title={isEnabled ? messages.deactivateMenuTitle : messages.activateMenuTitle}
                        onClick={onToggleBudgetIsEnabled}
                        disabled={isReadonly}
                    />

                    <Menu.Item
                        name={messages.showSettingsMenuName}
                        title={messages.showSettingsMenuTitle}
                        onClick={toggleIsShowSettingsPopup}
                        disabled={isAuditor || isReadonly}
                    />

                    {origin === domain.BudgetOrigin.Manual ? (
                        <>
                            <Menu.Item
                                name={messages.reuploadMenuName}
                                onClick={toggleIsShowReuploadPopup}
                                disabled={isAuditor || isReadonly}
                            />

                            <Menu.Item name={messages.downloadMenuName} onClick={onDownloadCsv} />

                            <Menu.Item
                                name={messages.removeMenuName}
                                onClick={onRemove}
                                disabled={isAuditor || isReadonly}
                            />
                        </>
                    ) : (
                        <Menu.Item
                            name={messages.refreshMenuName}
                            title={messages.refreshMenuTitle}
                            onClick={onRefresh}
                            disabled={!hasActiveIntegration || isReadonly}
                        />
                    )}
                </Menu>
            </Dropdown>

            <Popup open={isShowReuploadPopup} onToggle={toggleIsShowReuploadPopup} size='large'>
                <ReuploadPopupContent budgetId={budgetId} toggleIsOpenPopup={toggleIsShowReuploadPopup} />
            </Popup>

            <Popup open={!isNeedReview && isShowSettingsPopup} onToggle={toggleIsShowSettingsPopup}>
                <BudgetSettingsPopupContent budget={budget} toggleIsOpenPopup={toggleIsShowSettingsPopup} />
            </Popup>

            <Popup open={isNeedReview && isShowSettingsPopup} onToggle={toggleIsShowSettingsPopup}>
                <TrackingCategoriesPopup budget={budget} toggleIsOpenPopup={toggleIsShowSettingsPopup} />
            </Popup>
        </>
    );
});

export default BudgetActionsMenu;
